import React, { useEffect, useRef, useCallback, useState } from "react";
import { useTheme } from "@/contexts/ThemeContext.js";
import { saveUserInfo, getUserInfo, uploadProfilePicture } from "@/services/userService";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Lock } from "lucide-react";
import { User } from "firebase/auth";

// Define a type that combines Firebase User with our custom fields
type UserWithProfile = User & {
  firstName?: string;
  lastName?: string;
  // These are already in User type but we're including them to be explicit
  photoURL?: string | null;
  email?: string | null;
  displayName?: string | null;
  // Allow additional properties from Firestore
  [key: string]: any;
};
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUser } from "@/contexts/UserContextProvider";
import { UserOption } from "@/contexts/UsersListProvider";

export default function AccountSection() {
  const { isDark } = useTheme();
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [originalEmail, setOriginalEmail] = React.useState('');
  const [isPending, setIsPending] = React.useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [isPasswordPending, setIsPasswordPending] = React.useState(false);
  const [profilePicture, setProfilePicture] = React.useState('');
  const [profilePictureFile, setProfilePictureFile] = React.useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUpdates = useRef<Record<string, any>>({});
  const { user, setUser } = useUser();

  const handleSaveChanges = async () => {
    const success = await saveAccountInfo();
    if (success) {
      toast.success('Account information updated successfully');
    }
    return success;
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Profile picture must be less than 2MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    setProfilePictureFile(file);
    
    // Create a preview URL for the image
    const previewUrl = URL.createObjectURL(file);
    setProfilePicture(previewUrl);
  };

  const handlePasswordSubmit = async () => {
    if (!currentPassword.trim()) {
      toast.error('Please enter your current password');
      return;
    }
    
    setIsPasswordPending(true);
    try {
      const success = await saveAccountInfo(currentPassword);
      if (success) {
        setShowPasswordPrompt(false);
        setCurrentPassword('');
        toast.success('Email updated successfully');
      } else {
        throw new Error('Failed to update email');
      }
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update email. Please check your password and try again.');
    } finally {
      setIsPasswordPending(false);
    }
  };

  const handleRemoveProfilePicture = useCallback(() => {
    setProfilePicture('');
    setProfilePictureFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const saveAccountInfo = async (password?: string): Promise<boolean> => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter both first and last name');
      return false;
    }

    const updates: {
      displayName: string;
      email: string;
      photoURL: string | File | null;
      currentPassword?: string;
    } = {
      displayName: `${firstName.trim()} ${lastName.trim()}`,
      email: email.trim(),
      photoURL: profilePictureFile || profilePicture || null,
    };

    // Check if email is being changed
    const isEmailChanged = email.trim() !== originalEmail;
    
    // If email is being changed but no password provided, show password prompt
    if (isEmailChanged && !password) {
      pendingUpdates.current = updates;
      setShowPasswordPrompt(true);
      return false;
    }

    // If we have a password (either from prompt or not needed), proceed with save
    setIsPending(true);

    try {
      // If we have a password (from prompt), include it in updates
      if (password) {
        updates.currentPassword = password;
      }

      const result = await saveUserInfo(updates);
      
      if (result) {
        // If we had a file URL from a preview, revoke it now that we've saved
        if (profilePictureFile) {
          URL.revokeObjectURL(profilePicture);
          setProfilePictureFile(null);
        }
        // Update profile picture if it was changed
        if (result.photoURL) {
          setProfilePicture(result.photoURL);
          // Update UserContext 
          setUser([{
            id: result.uid,
            name: result.displayName || '',
            email: result.email || '',
            photoURL: result.photoURL || ''
          }]);  
        }
        // If email was changed, update the original email
        if (result.email) {
          setOriginalEmail(result.email);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving account information:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save account information');
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = await getUserInfo() as UserWithProfile | null;
        if (user) {
          // First try to get firstName/lastName directly
          if (user.firstName || user.lastName) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
          } else {
            // Fallback to splitting displayName if separate fields don't exist
            const nameParts = user.displayName?.split(' ') || [];
            setFirstName(nameParts[0] || '');
            setLastName(nameParts.slice(1).join(' ') || '');
          }
          const userEmail = user.email || '';
          setEmail(userEmail);
          setOriginalEmail(userEmail);
          setProfilePicture(user.photoURL || '');
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        toast.error('Failed to load user information');
      }
    };
    fetchUserInfo();

    // Cleanup function to revoke object URLs
    return () => {
      if (profilePicture && profilePicture.startsWith('blob:')) {
        URL.revokeObjectURL(profilePicture);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        <h2 className="text-xl font-semibold mb-4 text-foreground">Account Information</h2>
        <p className="text-sm text-muted-foreground mb-6">Manage your account details</p>

        <div className="space-y-4">

          {/* Profile Picture */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="profilePictureInput" className="block text-sm font-medium text-foreground">
                Profile Picture
              </label>
              {profilePicture && (
                <button
                  type="button"
                  onClick={handleRemoveProfilePicture}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Remove
                </button>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                  {profilePicture ? (
                    <img
                      src={profilePicture}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted-foreground/10 flex items-center justify-center text-muted-foreground">
                      <span className="text-xs">No photo</span>
                    </div>
                  )}
                </div>
                <label
                  htmlFor="profilePictureInput"
                  className="absolute -right-1 -bottom-1 bg-primary text-primary-foreground rounded-full p-1.5 cursor-pointer hover:bg-primary/90 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-camera"
                  >
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                    <circle cx="12" cy="13" r="3" />
                  </svg>
                  <span className="sr-only">Upload photo</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="profilePictureInput"
                  className="sr-only"
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  onChange={handleProfilePictureChange}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                <p>JPG, GIF, PNG, JPG, or WebP. Max 2MB.</p>
                <p>Recommended size: 400x400px</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="firstNameInput" className="block text-sm font-medium text-foreground">
              First Name
            </label>
            <input
              type="text"
              id="firstNameInput"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Enter your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This is your legal or preferred First Name.<br />
              For example, Harold
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="lastNameInput" className="block text-sm font-medium text-foreground">
              Last Name
            </label>
            <input
              type="text"
              id="lastNameInput"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This is your legal Last Name.<br />
              For example, Quackmore
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="emailInput" className="block text-sm font-medium text-foreground">
              Email Address
            </label>
            <input
              type="email"
              id="emailInput"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This email is used for account notifications and communications.
            </p>
          </div>

          <div className="pt-4">
            <Button 
              onClick={handleSaveChanges}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              disabled={isPending}
            >
              {isPending ? 'Saving...' : 'Save Account Information'}
            </Button>
          </div>
        </div>
      </div>

      {/* Password Prompt Dialog */}
      <Dialog open={showPasswordPrompt} onOpenChange={(open) => {
        if (!open) {
          // Reset the email to original value when dialog is closed
          setEmail(originalEmail);
        }
        setShowPasswordPrompt(open);
      }}>
        <DialogContent className="sm:max-w-[425px] bg-secondary">
          <DialogHeader>
            <DialogTitle>Verify Your Identity</DialogTitle>
            <DialogDescription>
              For security, please enter your current password to update your email address.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={isPasswordPending}
                placeholder="Enter your current password"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && currentPassword.trim()) {
                    await handlePasswordSubmit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPasswordPrompt(false);
                setCurrentPassword('');
                setEmail(originalEmail); // Reset email to original value
              }}
              disabled={isPasswordPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasswordSubmit}
              disabled={isPasswordPending || !currentPassword.trim()}
            >
              {isPasswordPending ? 'Verifying...' : 'Verify and Update Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    
  );
};
